import { create } from 'zustand';

import { CHAT_TILE_WIDTH, CHAT_TILE_WIDTH_WIDE } from '../constants';

type ChatOpenState = {
    readonly isOpen: boolean;
    /** Whether the dock is widened by any chat affordance that needs more horizontal room. */
    readonly isWide: boolean;
    /** Identifiers of change-proposal sections currently in compare mode. */
    readonly compareIds: readonly string[];
    /** Whether a Markdown table answer has manually requested the widened dock. */
    readonly isMarkdownTableWide: boolean;
    /**
     * Pending question answers selected in the composer but not yet submitted, keyed by
     * blockId then actionId. Shared so the thread can show each answered question (and reveal
     * the next one) while the composer asks the current question, before the backend echoes
     * the answered block back.
     */
    readonly blockAnswers: Readonly<Record<string, Readonly<Record<string, string>>>>;
    readonly close: () => void;
    readonly open: () => void;
    readonly toggle: () => void;
    readonly setMarkdownTableWide: (wide: boolean) => void;
    readonly setWide: (wide: boolean) => void;
    readonly addCompareId: (compareId: string) => void;
    readonly removeCompareId: (compareId: string) => void;
    readonly clearCompareIdsByPrefix: (prefix: string) => void;
    readonly setBlockAnswer: (blockId: string, actionId: string, answer: string) => void;
};

const shouldUseWideDock = (compareIds: readonly string[], isMarkdownTableWide: boolean): boolean =>
    compareIds.length > 0 || isMarkdownTableWide;

export const useChatStore = create<ChatOpenState>((set) => ({
    close: () =>
        set((state) => {
            if (!state.isOpen) {
                return state;
            }

            // Closing the chat also drops widened dock requests and any active compare.
            return {
                blockAnswers: {},
                compareIds: [],
                isMarkdownTableWide: false,
                isOpen: false,
                isWide: false,
            };
        }),
    blockAnswers: {},
    compareIds: [],
    isMarkdownTableWide: false,
    isOpen: false,
    isWide: false,
    open: () =>
        set((state) => {
            if (state.isOpen) {
                return state;
            }

            return { isOpen: true };
        }),
    // Entering compare widens the dock for side-by-side views; leaving all compares narrows back.
    addCompareId: (compareId) =>
        set((state) => {
            if (state.compareIds.includes(compareId)) return state;

            const compareIds = [...state.compareIds, compareId];
            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    clearCompareIdsByPrefix: (prefix) =>
        set((state) => {
            const compareIds = state.compareIds.filter((compareId) => !compareId.startsWith(prefix));
            if (compareIds.length === state.compareIds.length) return state;

            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    removeCompareId: (compareId) =>
        set((state) => {
            if (!state.compareIds.includes(compareId)) return state;

            const compareIds = state.compareIds.filter((id) => id !== compareId);
            return {
                compareIds,
                isWide: shouldUseWideDock(compareIds, state.isMarkdownTableWide),
            };
        }),
    setBlockAnswer: (blockId, actionId, answer) =>
        set((state) => ({
            blockAnswers: {
                ...state.blockAnswers,
                [blockId]: { ...state.blockAnswers[blockId], [actionId]: answer },
            },
        })),
    setMarkdownTableWide: (wide) =>
        set((state) =>
            state.isMarkdownTableWide === wide
                ? state
                : {
                      isMarkdownTableWide: wide,
                      isWide: shouldUseWideDock(state.compareIds, wide),
                  },
        ),
    setWide: (wide) =>
        set((state) =>
            state.isWide === wide
                ? state
                : {
                      isWide: wide,
                  },
        ),
    toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

export const useChatOpen = (): boolean => useChatStore((state) => state.isOpen);

export const useChatWidth = (): string =>
    useChatStore((state) => (state.isWide ? CHAT_TILE_WIDTH_WIDE : CHAT_TILE_WIDTH));
